import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Mic, Square, Play, Pause, Trash2 } from 'lucide-react';

export default function VoiceRecorder({ onRecordingComplete, onRecordingRemove, existingAudioUrl }) {
  const [state, setState] = useState('idle'); // idle | recording | recorded
  const [duration, setDuration] = useState(0);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(existingAudioUrl || null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      stopTimer();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      if (audioUrl && !existingAudioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, []);

  const stopTimer = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setState('recorded');
        stopTimer();
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        if (onRecordingComplete) {
          const file = new File([blob], `voice_note_${Date.now()}.webm`, { type: 'audio/webm' });
          onRecordingComplete(file);
        }
      };

      mediaRecorder.start(100);
      setState('recording');
      setDuration(0);
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } catch (err) {
      console.error('Microphone access denied:', err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  };

  const togglePlayback = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const removeRecording = () => {
    if (audioUrl && !existingAudioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setState('idle');
    setDuration(0);
    setPlaybackTime(0);
    setIsPlaying(false);
    if (onRecordingRemove) onRecordingRemove();
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current) setPlaybackTime(audioRef.current.currentTime);
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setPlaybackTime(0);
  };

  // Recording state: pulsing red mic
  if (state === 'recording') {
    return (
      <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3" data-testid="voice-recorder-recording">
        <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
        <span className="text-sm font-medium text-red-400 flex-1">Recording... {formatTime(duration)}</span>
        <Button type="button" size="sm" variant="destructive" onClick={stopRecording} className="h-8 px-3" data-testid="voice-stop-btn">
          <Square className="w-3.5 h-3.5 me-1" /> Stop
        </Button>
      </div>
    );
  }

  // Recorded state: playback controls
  if (state === 'recorded' || audioUrl) {
    const audioDuration = audioRef.current?.duration || duration;
    const progress = audioDuration > 0 ? (playbackTime / audioDuration) * 100 : 0;

    return (
      <div className="flex items-center gap-2 bg-muted/50 border border-border rounded-lg px-3 py-2" data-testid="voice-recorder-playback">
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={handleAudioTimeUpdate}
          onEnded={handleAudioEnded}
          preload="metadata"
        />
        <Button type="button" size="icon" variant="ghost" onClick={togglePlayback} className="w-8 h-8 flex-shrink-0" data-testid="voice-play-btn">
          {isPlaying ? <Pause className="w-4 h-4 text-brand" /> : <Play className="w-4 h-4 text-brand" />}
        </Button>
        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-brand rounded-full transition-all duration-200" style={{ width: `${progress}%` }} />
        </div>
        <span className="text-xs text-muted-foreground w-10 text-end flex-shrink-0">
          {formatTime(isPlaying ? playbackTime : audioDuration || duration)}
        </span>
        {!existingAudioUrl && (
          <Button type="button" size="icon" variant="ghost" onClick={removeRecording} className="w-7 h-7 flex-shrink-0 text-muted-foreground hover:text-red-500" data-testid="voice-remove-btn">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    );
  }

  // Idle state: mic button
  return (
    <Button type="button" variant="outline" size="sm" onClick={startRecording} className="h-9 gap-2 text-muted-foreground hover:text-brand hover:border-brand/50" data-testid="voice-record-btn">
      <Mic className="w-4 h-4" /> Record Voice Note
    </Button>
  );
}
